// GalaxyDust.tsx — Thousands of faint background stars scattered along the same
// two-arm spiral as the real systems, so the map reads as a dense galaxy (à la
// Stellaris) rather than 220 lone dots. Pure decoration: one additive Points draw
// call, non-interactive, deterministic (fixed seed). The 220 gameplay stars
// (StarMarker) render brighter on top.
import { useMemo } from 'react';
import { AdditiveBlending, BufferGeometry, Color, Float32BufferAttribute } from 'three';
import { makeRng } from '../galaxy/rng';
import { glowTexture } from './glow';

const ARMS = 2;
const TURNS = 2.4;
const ARM_SPREAD = 0.45;
const COUNT = 5200;

// Faint blue-white population with a warm minority — dimmer than the real stars.
const TINTS = ['#bcd0ff', '#dfe8ff', '#ffffff', '#ffe6bf', '#ffd0a0', '#cfe0ff'];

function buildGeometry(): BufferGeometry {
  const rng = makeRng(0x5a_1d_2f);
  const positions = new Float32Array(COUNT * 3);
  const colors = new Float32Array(COUNT * 3);
  const c = new Color();
  for (let i = 0; i < COUNT; i++) {
    const arm = i % ARMS;
    const t = Math.pow(rng.next(), 0.5);
    const angle = t * TURNS * Math.PI * 2 + (arm / ARMS) * Math.PI * 2 + rng.range(-0.25, 0.25);
    const dist = 3 + t * 53;
    const scatter = ARM_SPREAD * dist * 0.18;
    positions[i * 3] = Math.cos(angle) * dist + rng.range(-scatter, scatter);
    positions[i * 3 + 1] = rng.range(-2.4, 2.4) * (1 - t * 0.5);
    positions[i * 3 + 2] = Math.sin(angle) * dist + rng.range(-scatter, scatter);
    // Dimmer toward the rim; a little brightness jitter so it twinkles.
    const b = (0.45 + rng.next() * 0.55) * (1 - t * 0.35);
    c.set(rng.pick(TINTS)).multiplyScalar(b);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geo.setAttribute('color', new Float32BufferAttribute(colors, 3));
  return geo;
}

export function GalaxyDust() {
  const geometry = useMemo(buildGeometry, []);
  const tex = useMemo(glowTexture, []);
  return (
    <points geometry={geometry} renderOrder={-5}>
      <pointsMaterial
        map={tex}
        size={0.9}
        sizeAttenuation
        vertexColors
        transparent
        opacity={0.85}
        depthWrite={false}
        blending={AdditiveBlending}
        toneMapped={false}
      />
    </points>
  );
}
